import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  jobPostingsApi,
  jobCategoriesApi,
} from "proxyUrl";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString();
  } catch (err) {
    return value;
  }
};

const JobPostingList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [postings, setPostings] = useState([]);
  const [filters, setFilters] = useState({
    title: "",
    status: "",
    category: "",
    type: "",
    assignedTo: "",
    postedBy: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    assignedToUsers: [],
    postedByNames: [],
    jobTypes: [],
    statusOptions: [],
  });

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
    
  
  const userPermissions = loggedUser.userPermission || [];
  // console.log('userPermissions', userPermissions);
  const canViewJob = userPermissions.includes("view Job posting");
  const canCreateJob = userPermissions.includes("create Job posting");
  const canUpdateJob = userPermissions.includes("update Job posting");
  const canDeleteJob = userPermissions.includes("delete Job posting");

  const fetchPostings = async (customFilters = null) => {
    setLoading(true);
    try {
      const activeFilters = customFilters !== null ? customFilters : filters;
      const params = {};
      if (activeFilters.title && activeFilters.title.trim()) {
        params.title = activeFilters.title.trim();
      }
      if (activeFilters.status && activeFilters.status.trim()) {
        params.status = activeFilters.status.trim();
      }
      if (activeFilters.category) {
        params.category = activeFilters.category;
      }
      if (activeFilters.type && activeFilters.type.trim()) {
        params.type = activeFilters.type.trim();
      }
      if (activeFilters.assignedTo) {
        params.assignedTo = activeFilters.assignedTo;
      }
      if (activeFilters.postedBy && activeFilters.postedBy.trim()) {
        params.postedBy = activeFilters.postedBy.trim();
      }
      
      console.log("Fetching with params:", params);
      
      const { data } = await axios.get(jobPostingsApi, {
        params,
        withCredentials: true,
      });
      setPostings(data?.data || []);
    } catch (error) {
      console.error("jobPostings.list ui error", error);
      toast.error("Unable to load job postings");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const { data } = await axios.get(`${jobPostingsApi}/filter-options`, {
        withCredentials: true,
      });
      setFilterOptions(data?.data || {
        categories: [],
        assignedToUsers: [],
        postedByNames: [],
        jobTypes: [],
        statusOptions: [],
      });
    } catch (error) {
      console.error("jobPostings.filterOptions ui error", error);
      toast.error("Unable to load filter options");
    }
  };

  useEffect(() => {
    fetchPostings();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update filters when they change
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      title: "",
      status: "",
      category: "",
      type: "",
      assignedTo: "",
      postedBy: "",
    };
    setFilters(emptyFilters);
    fetchPostings(emptyFilters);
  };

  const filteredPostings = useMemo(() => postings, [postings]);

  const handleDelete = async (id) => {
    if (!canDeleteJob) {
      toast.error("You do not have permission to delete job postings");
      return;
    }
    const confirmDelete = window.confirm(
      "Delete this job posting? This will also remove its leads."
    );
    if (!confirmDelete) return;
    try {
      await axios.delete(`${jobPostingsApi}/${id}`, { withCredentials: true });
      toast.success("Job posting deleted");
      setPostings((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("jobPosting.delete ui error", error);
      toast.error("Delete failed");
    }
  };

  return (
    <>
      {!canViewJob ? (
        <div className="rounded-xl bg-white p-6 text-center text-sm text-red-600 shadow">
          You are not authorized to view job postings.
        </div>
      ) : (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-2xl bg-indigo-600 p-4 text-white shadow">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">
            Hiring
          </p>
          <h1 className="text-xl font-semibold">Job Postings</h1>
        </div>
        {canCreateJob && (
          <button
            onClick={() => navigate("/admin/job-postings/create")}
            className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-primaryBlue shadow hover:shadow-md"
          >
            Create Job Posting
          </button>
        )}
      </div>

      <div className="rounded-xl bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-2">
          {/* Title Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Title
            </label>
            <input
              type="text"
              placeholder="Search title"
              value={filters.title}
              onChange={(e) => updateFilter("title", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="">All Status</option>
              {filterOptions.statusOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => updateFilter("category", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="">All Categories</option>
              {filterOptions.categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => updateFilter("type", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="">All Types</option>
              {filterOptions.jobTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned To Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Assigned To
            </label>
            <select
              value={filters.assignedTo}
              onChange={(e) => updateFilter("assignedTo", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="">All Users</option>
              {filterOptions.assignedToUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name || `User #${user.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Posted By Filter */}
          <div className="flex-1 min-w-[120px]">
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Posted By
            </label>
            <select
              value={filters.postedBy}
              onChange={(e) => updateFilter("postedBy", e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            >
              <option value="">All Posted By</option>
              {filterOptions.postedByNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fetchPostings();
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:shadow-md whitespace-nowrap"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResetFilters();
              }}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Salary
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Resources
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Assigned To
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Updated
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Posted By
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {!loading && filteredPostings.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    No job postings found.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {filteredPostings.map((job) => (
                <tr key={job.id}>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-primaryBlue">
                        {(job.title || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{job.title}</p>
                        <p className="text-xs text-gray-500">{job.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        job.jobStatus === "Open"
                          ? "bg-green-100 text-green-700"
                          : job.jobStatus === "Closed"
                          ? "bg-gray-200 text-gray-700"
                          : job.jobStatus === "Position Filled"
                          ? "bg-blue-100 text-blue-700"
                          : job.jobStatus === "Interviewing"
                          ? "bg-yellow-100 text-yellow-700"
                          : job.jobStatus === "Offer in Progress"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-indigo-100 text-indigo-700"
                      }`}
                    >
                      {job.jobStatus || "Open"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {filterOptions.categories.find((c) => c.id == job.jobCategoryId)?.name ||
                      "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {job.minSalary || job.maxSalary
                      ? `${job.minSalary || "-"} - ${job.maxSalary || "-"}`
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {job.jobType || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {job.numberOfResources !== undefined &&
                    job.numberOfResources !== null &&
                    job.numberOfResources !== ""
                      ? job.numberOfResources
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {job.assignedUser?.name ||
                      job.assignedUser?.email ||
                      (job.assignedTo ? `User #${job.assignedTo}` : "Unassigned")}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {formatDate(job.updatedAt || job.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {job.requestByName || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link
                        to={`/admin/job-postings/${job.id}/preview`}
                        className="flex items-center justify-center rounded-lg bg-indigo-50 p-2 text-indigo-600 transition-all duration-200 hover:bg-indigo-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                        title="Preview"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </Link>
                      <Link
                        to={`/admin/job-postings/${job.id}/leads`}
                        className="flex items-center justify-center rounded-lg bg-green-50 p-2 text-green-600 transition-all duration-200 hover:bg-green-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                        title="Leads"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                          />
                        </svg>
                      </Link>
                      {canUpdateJob && (
                        <Link
                          to={`/admin/job-postings/${job.id}/edit`}
                          className="flex items-center justify-center rounded-lg bg-amber-50 p-2 text-amber-600 transition-all duration-200 hover:bg-amber-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                            />
                          </svg>
                        </Link>
                      )}
                      {canDeleteJob && (
                        <button
                          onClick={() => handleDelete(job.id)}
                          className="flex items-center justify-center rounded-lg bg-red-50 p-2 text-red-600 transition-all duration-200 hover:bg-red-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
 
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ToastContainer />
    </div>
      )}
    </>
  );
};

export default JobPostingList;

